import { useState } from 'react';
import { useIntl } from 'react-intl';

import { slate } from '@/lib/colours';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LoadingButton,
  Typography,
} from '@/primitives';
import { surveyMessages } from './messages';

export interface SurveyDialogProps {
  loading?: boolean;
  onClickSubmit: (checkedIds: string[]) => void;
  onClickSkip: () => void;
  open: boolean;
}

export function SurveyDialog({
  loading,
  onClickSubmit,
  onClickSkip,
  open,
}: SurveyDialogProps) {
  const intl = useIntl();

  const [checkedIds, setCheckedIds] = useState<string[]>([]);

  const surveyHeading = surveyMessages['survey.users.heading'];
  const surveySubHeading = surveyMessages['survey.users.subHeading'];
  const surveySkipButtonText = surveyMessages['survey.users.skipButton'];
  const surveySubmitButtonText = surveyMessages['survey.users.submitButton'];
  const surveySubmittingButtonText =
    surveyMessages['survey.users.submittingButtonText'];
  const surveyQuestions = surveyMessages['survey.users.questions'];

  const handleCheckboxChange = (questionId: string) => {
    const updatedIds = checkedIds.includes(questionId)
      ? checkedIds.filter((id) => id !== questionId)
      : [...checkedIds, questionId];

    setCheckedIds(updatedIds);
  };

  const handleSubmit = () => {
    onClickSubmit(checkedIds);
  };

  return (
    <Dialog
      open={open}
      disablePortal
      sx={{
        '.MuiDialog-paper': {
          maxHeight: '60vh',
        },
      }}
    >
      <DialogTitle component="div" sx={{ paddingBottom: '12px' }}>
        <Typography variant="headlinesmall" sx={{ paddingBottom: '16px' }}>
          {surveyHeading}
        </Typography>
        <Typography variant="bodymedium" color={slate[500]}>
          {surveySubHeading}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {surveyQuestions.map((question: any) => (
            <Box
              key={question.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 0',
              }}
            >
              <Box
                key={question.id}
                sx={{
                  display: 'flex',
                  padding: '4px 16px',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '6px',
                  flex: '1 0 0',
                  borderRadius: '8px',
                  border: `1px solid ${slate[300]}`,
                }}
              >
                <Checkbox onChange={() => handleCheckboxChange(question.id)} />
                <Typography
                  variant="bodysmall"
                  component="div"
                  color={slate[900]}
                >
                  {question.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClickSkip}>{surveySkipButtonText}</Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          loadingText={intl.formatMessage(surveySubmittingButtonText.props)}
          onClick={handleSubmit}
        >
          {surveySubmitButtonText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
